import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { MapPin, Loader2, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// name + Zippopotam/postcodes.io country code
export const COUNTRIES = [
  { code: "gb", name: "United Kingdom" },
  { code: "us", name: "United States" },
  { code: "ca", name: "Canada" },
  { code: "au", name: "Australia" },
  { code: "nz", name: "New Zealand" },
  { code: "ie", name: "Ireland" },
  { code: "de", name: "Germany" },
  { code: "fr", name: "France" },
  { code: "es", name: "Spain" },
  { code: "it", name: "Italy" },
  { code: "pt", name: "Portugal" },
  { code: "nl", name: "Netherlands" },
  { code: "be", name: "Belgium" },
  { code: "ch", name: "Switzerland" },
  { code: "at", name: "Austria" },
  { code: "dk", name: "Denmark" },
  { code: "se", name: "Sweden" },
  { code: "no", name: "Norway" },
  { code: "fi", name: "Finland" },
  { code: "pl", name: "Poland" },
  { code: "cz", name: "Czechia" },
  { code: "jp", name: "Japan" },
  { code: "in", name: "India" },
  { code: "br", name: "Brazil" },
  { code: "mx", name: "Mexico" },
  { code: "za", name: "South Africa" },
];

function buildValue(countryName, place, region) {
  const parts = [];
  [place, region].forEach((p) => {
    if (p && !parts.includes(p)) parts.push(p);
  });
  return parts.length ? `${parts.join(", ")}, ${countryName}` : countryName;
}

export default function SessionLocationField({ label, value, editMode, onChange, testId }) {
  const [countryCode, setCountryCode] = useState("");
  const [postcode, setPostcode] = useState("");
  const [resolved, setResolved] = useState(null); // {region, place}
  const [status, setStatus] = useState("idle"); // idle | loading | ok | error
  const [errorMsg, setErrorMsg] = useState("");
  const debounceRef = useRef(null);
  const initedRef = useRef(false);

  // Preselect the country from the stored value ONCE when entering edit mode
  useEffect(() => {
    if (!editMode) { initedRef.current = false; return; }
    if (initedRef.current) return;
    initedRef.current = true;
    const match = COUNTRIES.find((c) => (value || "").toLowerCase().includes(c.name.toLowerCase()))
      || ((value || "").toLowerCase().includes("uk") ? COUNTRIES[0] : null);
    setCountryCode(match ? match.code : "");
    setPostcode("");
    setResolved(null);
    setStatus("idle");
    setErrorMsg("");
  }, [editMode, value]);

  const countryName = (code) => COUNTRIES.find((c) => c.code === code)?.name || "";

  const runLookup = async (code, pc) => {
    setStatus("loading");
    setErrorMsg("");
    try {
      const { data } = await axios.get(`${API}/geo/lookup`, { params: { country: code, postcode: pc }, headers: { Authorization: `Bearer ${localStorage.getItem("ldc_token")}` } });
      setResolved({ region: data.region, place: data.place });
      setStatus("ok");
      onChange(buildValue(countryName(code), data.place, data.region));
    } catch (e) {
      setResolved(null);
      setStatus("error");
      setErrorMsg(e.response?.status === 404 ? "Postcode not found — check and try again." : "Lookup failed. Try again.");
      onChange(countryName(code)); // at least keep the country
    }
  };

  const onCountryChange = (code) => {
    setCountryCode(code);
    setResolved(null);
    setStatus("idle");
    onChange(code ? countryName(code) : "");
    if (code && postcode.trim().length >= 3) runLookup(code, postcode.trim());
  };

  const onPostcodeChange = (pc) => {
    setPostcode(pc);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (countryCode && pc.trim().length >= 3) {
      debounceRef.current = setTimeout(() => runLookup(countryCode, pc.trim()), 600);
    } else {
      setStatus("idle");
      setResolved(null);
    }
  };

  if (!editMode) {
    return (
      <div className="field-group">
        <label className="field-label">{label}</label>
        <div className="field-value" data-testid={`display-${testId}`}>
          {value ? value : <span className="field-empty">Not set</span>}
        </div>
      </div>
    );
  }

  return (
    <div className="field-group" data-testid={`${testId}-editor`}>
      <label className="field-label">{label}</label>
      <div className="loc-grid">
        <select
          className="field-input"
          value={countryCode}
          onChange={(e) => onCountryChange(e.target.value)}
          data-testid={`${testId}-country`}
        >
          <option value="">Select country…</option>
          {COUNTRIES.map((c) => (
            <option key={c.code} value={c.code}>{c.name}</option>
          ))}
        </select>
        <input
          className="field-input"
          value={postcode}
          onChange={(e) => onPostcodeChange(e.target.value)}
          placeholder={countryCode ? "Post / ZIP code" : "Select a country first"}
          disabled={!countryCode}
          data-testid={`${testId}-postcode`}
        />
      </div>
      <div className="loc-status">
        {status === "loading" && (
          <span className="loc-loading"><Loader2 size={13} className="spin" /> Looking up region…</span>
        )}
        {status === "ok" && resolved && (
          <span className="loc-ok" data-testid={`${testId}-resolved`}>
            <Check size={13} /> {[resolved.place, resolved.region].filter(Boolean).join(", ") || "Region set"}
          </span>
        )}
        {status === "error" && <span className="loc-error">{errorMsg}</span>}
        {status === "idle" && countryCode && (
          <span className="loc-hint"><MapPin size={12} /> Enter a post/ZIP code to auto-fill the region.</span>
        )}
      </div>
    </div>
  );
}
